<template>
  <div>
    <app-spinner v-if="!is_loaded"/>
    <div v-else class="company">
      <div class="company__wrapper">
        <app-cells>
          <h1 class="title">{{ company.name }}</h1>
        </app-cells>
        <div class="info-cards">
          <div class="info-cards__card">
            <button v-if="is_transfer_supervisor" class="info-cards__control" @click="onShowUpdateCompanyPopup">
              <img alt="" src="@/assets/img/edit-table-icon.svg">
            </button>
            <div class="info-cards__row">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Car models:</span><span>{{ company.car_models.map(item => item.short_name).join(', ') }}</span>
              </div>
            </div>
            <div class="info-cards__row">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Cars:</span><span>{{ company.cars_count }}</span>
              </div>
            </div>
            <div class="info-cards__row">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Drivers:</span><span>{{ company.drivers_count }}</span>
              </div>
            </div>
          </div>
          <div class="info-cards__card">
            <div class="info-cards__row">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Total ordered:</span><span>{{ company.total_ordered.toLocaleString() }} TSh</span>
              </div>
            </div>
            <div class="info-cards__row">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Total unpaid:</span><span>{{ company.total_unpaid.toLocaleString() }} TSh</span>
              </div>
            </div>
            <div class="info-cards__row">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Total paid:</span><span>{{ company.total_paid.toLocaleString() }} TSh</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <app-cells>
        <h2 class="title">Drivers</h2>
        <app-button v-if="is_transfer_supervisor" size="small" @click.native="onShowCreateModal('driver')">Add driver
        </app-button>
      </app-cells>
      <v-client-table
        :class="{ 'table-default--loading': table_drivers_preloader }"
        :columns="table_drivers.columns"
        :data="table_drivers.items"
        :options="table_drivers.options"
        class="table-default"
      >
        <template #h__first_name>
          <div class="table-default__left">Name</div>
        </template>
        <template #first_name="{ row }">
          <div class="table-default__left">{{ row.first_name }} {{ row.last_name || '' }}</div>
        </template>
        <template #communication="{ row }">
          <div class="table-default__controls">
            <button
              v-tooltip="{content: row.phone, placement: 'top-start', offset: 10}"
              :class="{'table-default__activity--active': row.phone}"
              class="table-default__activity table-default__activity--phone"
              @click="onCopyContactLink(row.phone)"
            >
            </button>
            <a
              :class="{'table-default__activity--active': row.phone_telegram}"
              :href="`https://t.me/${row.phone}`"
              class="table-default__activity table-default__activity--tg"
              target="_blank"
            >
            </a>
            <a
              :class="{'table-default__activity--active': row.phone_whatsapp}"
              :href="`https://wa.me/${row.phone}`"
              class="table-default__activity table-default__activity--wa"
              target="_blank"
            >
            </a>
          </div>
        </template>
        <template #communication_other="{ row }">
          <div class="table-default__controls">
            <button
              v-tooltip="{content: row.additional_phone, placement: 'top-start', offset: 10}"
              :class="{'table-default__activity--active': row.additional_phone}"
              class="table-default__activity table-default__activity--phone"
              @click="onCopyContactLink(row.phone)"
            >
            </button>
            <a
              :class="{'table-default__activity--active': row.additional_phone_telegram}"
              :href="`https://t.me/${row.additional_phone}`"
              class="table-default__activity table-default__activity--tg"
              target="_blank"
            >
            </a>
            <a
              :class="{'table-default__activity--active': row.additional_phone_whatsapp}"
              :href="`https://wa.me/${row.additional_phone}`"
              class="table-default__activity table-default__activity--wa"
              target="_blank"
            >
            </a>
          </div>
        </template>
        <template #controls="{ row }">
          <div v-if="is_transfer_supervisor" class="table-default__controls">
            <button type="button" @click="onShowEditDriverModal(row)">
              <img alt="" src="@/assets/img/edit-table-icon.svg">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            v-if="pagination_drivers.pages > 1"
            v-model="pagination_drivers.page"
            :clickHandler="onClickPaginationDrivers"
            :container-class="'pagination'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :next-text="'>'"
            :page-class="'pagination__item'"
            :page-count="pagination_drivers.pages"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :prev-link-class="'pagination__link pagination__link--prev'"
            :prev-text="'<'"
          />
        </div>
        <app-table-records :all="pagination_drivers.count" :count="table_drivers.items.length"
                           :page="pagination_drivers.page"/>
      </app-cells>
      <app-cells>
        <h2 class="title">Cars</h2>
        <app-button v-if="is_transfer_supervisor" size="small" @click.native="onShowCreateModal('car')">Add car
        </app-button>
      </app-cells>
      <v-client-table
        :class="{ 'table-default--loading': table_cars_preloader }"
        :columns="table_cars.columns"
        :data="table_cars.items"
        :options="table_cars.options"
        class="table-default"
      >
        <template #h__car>
          <div class="table-default__left">Car model</div>
        </template>
        <template #h__driver>
          <div class="table-default__left">Driver</div>
        </template>
        <template #car="{ row }">
          <div class="table-default__left">{{ row.car_model.name }} ({{ row.plate_number }})</div>
        </template>
        <template #driver="{ row }">
          <div class="table-default__left">{{ row.driver.first_name }} {{ row.driver.last_name || '' }}</div>
        </template>
        <template #controls="{ row }">
          <div v-if="is_transfer_supervisor" class="table-default__controls">
            <button type="button" @click="onShowEditCarModal(row)">
              <img alt="" src="@/assets/img/edit-table-icon.svg">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            v-if="pagination_cars.pages > 1"
            v-model="pagination_cars.page"
            :clickHandler="onClickPaginationCars"
            :container-class="'pagination'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :next-text="'>'"
            :page-class="'pagination__item'"
            :page-count="pagination_cars.pages"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :prev-link-class="'pagination__link pagination__link--prev'"
            :prev-text="'<'"
          />
        </div>
        <app-table-records :all="pagination_cars.count" :count="table_cars.items.length" :page="pagination_cars.page"/>
      </app-cells>
      <app-cells><h2 class="title">List of transfers</h2></app-cells>
      <app-cells>
        <app-input
          v-model="search"
          class="input--300"
          name="search"
          placeholder="Search by name"
          type="search"
          @input.native="onListSearch"
        />
        <app-filters
          v-model="filter"
          :filters="filters"
          name="filterHall"
          @change="onFilterChange"
        />
      </app-cells>
      <v-client-table
        :class="{ 'table-default--loading': table_transfers_preloader }"
        :columns="table_transfers.columns"
        :data="table_transfers.items"
        :options="table_transfers.options"
        class="table-default"
      >
        <template #h__select>
          <label
            v-if="table_transfers.items.length && table_transfers.items.some(item => !item.invoice)"
            class="checkbox checkbox--table"
          >
            <input
              v-model="checked_all"
              type="checkbox"
              name="checked_all"
            />
            <span class="checkbox__checkmark"></span>
          </label>
        </template>
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #h__from>
          <div class="table-default__left">From</div>
        </template>
        <template #h__to>
          <div class="table-default__left">To</div>
        </template>
        <template #h__car>
          <div class="table-default__left">Car</div>
        </template>
        <template #h__driver>
          <div class="table-default__left">Driver</div>
        </template>
        <template #select="{ row }">
          <label v-if="!row.invoice" class="checkbox checkbox--table">
            <input
              v-model="checked_items"
              type="checkbox"
              :value="row"
            />
            <span class="checkbox__checkmark"></span>
          </label>
        </template>
        <template #price="{ row }">{{ row.rental_cost.toLocaleString() }} TSh</template>
        <template #date="{ row }">{{ dateFormat(row.transfer.pick_up_date) }} {{ row.transfer.pick_up_time }}</template>
        <template #name="{ row }">
          <div class="table-default__svg-text">
            <span v-html="row.transfer.type.icon"></span>
            {{ row.transfer.name }} ({{ row.transfer.pax }})
          </div>
        </template>
        <template #from="{ row }">
          <div class="table-default__left">{{ row.transfer.pick_up_point.short_name }}</div>
        </template>
        <template #to="{ row }">
          <div class="table-default__left">
            {{ row.transfer.points[row.transfer.points.length - 1].location.short_name }}
          </div>
        </template>
        <template #car="{ row }">
          <div class="table-default__left">
            {{ row.car.car_model.short_name }} ({{ row.car.plate_number }})
          </div>
        </template>
        <template #driver="{ row }">
          <div class="table-default__left">{{ row.driver.full_name }}</div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            v-if="pagination_transfers.pages > 1"
            v-model="pagination_transfers.page"
            :clickHandler="onClickPaginationTransfers"
            :container-class="'pagination'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :next-text="'>'"
            :page-class="'pagination__item'"
            :page-count="pagination_transfers.pages"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :prev-link-class="'pagination__link pagination__link--prev'"
            :prev-text="'<'"
          />
        </div>
        <app-table-records :all="pagination_transfers.count" :count="table_transfers.items.length"
                           :page="pagination_transfers.page"/>
      </app-cells>
      <app-cells><h2 class="title">Invoices</h2></app-cells>
      <v-client-table
        :data="table_invoices.items"
        :columns="table_invoices.columns"
        :options="table_invoices.options"
        class="table-default"
        :class="{ 'table-default--loading': table_invoices_preloader }"
      >
        <template #h__supplier><div class="table-default__left">Supplier</div></template>
        <template #h__responsible><div class="table-default__left">Responsible</div></template>
        <template #date="{ row }">{{ dateFormat(row.supplier_invoice.date) }}</template>
        <template #supplier="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'administrative-suppliers-detail-purchases', params: { id: row.supplier_invoice.supplier.id } }"
              class="table-default__link-inline"
            >
              {{ row.supplier_invoice.supplier.legal_name }}
            </router-link>
          </div>
        </template>
        <template #responsible="{ row }"><div class="table-default__left">{{ row.responsible?.full_name || '-' }}</div></template>
        <template #amount="{ row }">
          {{ amountFormat(row.supplier_invoice.amount_total) }} {{ row.supplier_invoice.currency.code }}
        </template>
        <template #invoice_number="{ row }">{{ row.supplier_invoice.invoice_number || '-' }}</template>
        <template #payment_method="{ row }">{{ row.supplier_invoice.supplier_payment_method.payment_method.name || '-' }}</template>
        <template #account="{ row }">
          <template v-if="row.supplier_invoice.supplier_payment_method.payment_method.code === 'bank'">
            <template v-if="row.supplier_invoice.supplier_payment_method.bank_account">
              {{ row.supplier_invoice.supplier_payment_method.bank_account.account_number }}<br>{{ row.supplier_invoice.supplier_payment_method.bank_account.bank.short_name }}
            </template>
            <template v-else>-</template>
          </template>
          <template v-else-if="row.supplier_invoice.supplier_payment_method.payment_method.code === 'mobile'">
            <template v-if="row.supplier_invoice.supplier_payment_method.mobile_account">
              {{ row.supplier_invoice.supplier_payment_method.mobile_account.account_number }}<br>{{ row.supplier_invoice.supplier_payment_method.mobile_account.provider.name }}
            </template>
            <template v-else>-</template>
          </template>
          <template v-else-if="row.supplier_invoice.supplier_payment_method.payment_method.code === 'gepg'">
            <template v-if="row.supplier_invoice.gepg_number">{{ row.gepg_number }}</template>
            <template v-else>-</template>
          </template>
          <template v-else>-</template>
        </template>
        <template #payment_plans="{ row }">
          <span
            :style="{ 'backgroundColor': row.supplier_invoice.payment_status === 'unpaid' ? '#e85c20' : row.supplier_invoice.payment_status === 'paid' ? '#77b017' : '#439ad1' }"
            class="status-tag"
          >
            {{ row.supplier_invoice.payment_status === 'unpaid' ? 'Unpaid' : row.supplier_invoice.payment_status === 'paid' ? 'Paid' : amountFormat(row.supplier_invoice.paid_amount) }}
          </span>
        </template>
        <template #attachment="{ row }">
          <div class="table-default__controls">
            <a
              :href="row.attachment?.file"
              target="_blank"
              :class="{'table-default__disabled': !row.attachment}"
            >
              <img
                src="@/assets/img/snacks-link.svg"
                alt="Attachment"
              >
            </a>
          </div>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowDeleteInvoiceModal(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationInvoices"
            v-if="pagination_invoices.pages > 1"
            v-model="pagination_invoices.page"
            :page-count="pagination_invoices.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_invoices.count" :count="table_invoices.items.length" :page="pagination_invoices.page" />
      </app-cells>
      <div class="flying-block" :class="{ 'flying-block--show': is_show_flying_block }">
        <div class="flying-block__text">
          Selected transfers:
          <template v-if="checked_items.length"><span>{{ checked_items.length }}</span> ({{ checkedSum ? `${amountFormat(checkedSum)} TZS` : '' }})</template>
        </div>
        <app-button @click.native="onShowModalRequestPayment">Request payment</app-button>
      </div>
    </div>
    <modal
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :scrollable="true"
      :width="400"
      class="modal-not-overflow"
      name="driver-form"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('driver-form')"></button>
        <div class="popup__title">{{ is_create ? 'Adding' : 'Editing' }} driver</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFormDriver">
            <app-form-group label="First name" label-for="first_name" required>
              <app-input
                id="first_name"
                v-model="form_driver.first_name"
                :error="$v.form_driver.first_name.$error"
                name="first_name"
                placeholder="First name"
                @change.native="$v.form_driver.first_name.$touch()"
              />
              <template #error>
                <div v-if="$v.form_driver.first_name.$dirty && !$v.form_driver.first_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Last name" label-for="last_name">
              <app-input
                id="last_name"
                v-model="form_driver.last_name"
                name="last_name"
                placeholder="Last name"
              />
            </app-form-group>
            <app-form-group label="Phone number">
              <app-phone
                v-model="form_driver.phone"
                placeholder="+7 499 113 28 00"
              />
            </app-form-group>
            <app-form-group label="Other communications">
              <app-cells :indent="false">
                <app-checkbox v-model="form_driver.phone_telegram" :readonly="!form_driver.phone" label="Telegram"/>
                <app-checkbox v-model="form_driver.phone_whatsapp" :readonly="!form_driver.phone" label="Whatsapp"/>
              </app-cells>
            </app-form-group>
            <app-form-group label="Phone number (additional)">
              <app-phone
                v-model="form_driver.additional_phone"
                placeholder="+7 499 113 28 00"
              />
            </app-form-group>
            <app-form-group label="Other communications">
              <app-cells :indent="false">
                <app-checkbox v-model="form_driver.additional_phone_telegram" :readonly="!form_driver.additional_phone"
                              label="Telegram"/>
                <app-checkbox v-model="form_driver.additional_phone_whatsapp" :readonly="!form_driver.additional_phone"
                              label="Whatsapp"/>
              </app-cells>
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button ref="submit_driver" :disabled="$v.form_driver.$error" type="submit">
                {{ is_create ? 'Add driver' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :scrollable="true"
      :width="400"
      class="modal-not-overflow"
      name="car-form"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('car-form')"></button>
        <div class="popup__title">{{ is_create ? 'Adding' : 'Editing' }} car</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFormCar">
            <app-form-group label="Car model" required>
              <app-select
                v-model="form_car.car_model"
                :clearable="false"
                :error="$v.form_car.car_model.$error"
                :options="cars_options"
                :reduce="item => item.id"
                placeholder="Car model"
              />
              <template #error>
                <div v-if="$v.form_car.car_model.$dirty && !$v.form_car.car_model.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Plate number" label-for="plate_number" required>
              <app-input
                id="plate_number"
                v-model="form_car.plate_number"
                :error="$v.form_car.plate_number.$error"
                name="plate_number"
                placeholder="Plate number"
                @change.native="$v.form_car.plate_number.$touch()"
              />
              <template #error>
                <div v-if="$v.form_car.plate_number.$dirty && !$v.form_car.plate_number.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Driver">
              <app-select
                v-model="form_car.driver"
                :clearable="false"
                :options="company_drivers"
                :reduce="item => item.id"
                label="first_name"
                placeholder="Driver"
              />
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button ref="submit_car" :disabled="$v.form_car.$error" type="submit">
                {{ is_create ? 'Add car' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :scrollable="true"
      :width="400"
      class="modal-not-overflow"
      name="delete-modal"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('delete-modal')"></button>
        <div class="popup__title popup__title--flex">
          <img alt="" src="@/assets/img/warning-round-icon.svg">
          Delete Record
        </div>
        <div class="popup__body">
          <div class="popup__subt">
            Are you sure you want to delete this {{ current_name === 'driver' ? 'driver' : 'car' }}?
          </div>
          <app-cells :indent="false" position="center">
            <app-button theme="dark" @click.native="$modal.hide('delete-modal')">Cancel</app-button>
            <app-button ref="delete_button" @click.native="onDeleteItem">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      :width="400"
      class="modal-not-overflow"
      draggable=".popup__title"
      name="update-company"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('update-company')"></button>
        <div class="popup__title">Update company</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Name" label-for="company_name" required>
              <app-input
                id="company_name"
                v-model="form.name"
                :error="$v.form.name.$error"
                name="company_name"
                placeholder="Name"
                @change.native="$v.form.name.$touch()"
              />
              <template #error>
                <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Car models" required>
              <app-select
                v-model="form.car_models"
                :clearable="false"
                :error=" $v.form.car_models.$error"
                :options="cars_options"
                :reduce="item => item.id"
                multiple
                placeholder="Car models"
              />
              <template #error>
                <div v-if="$v.form.car_models.$dirty && !$v.form.car_models.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Supplier" required>
              <app-select
                @search="onSupplierSearch"
                @input="onSupplierSelected"
                v-model="form.supplier"
                :options="supplier_options"
                :clearable="false"
                :display-field="option => `${option.trade_name} ${option.tin ? `(TIN: ${option.tin})` : ''}`"
                label="trade_name"
                placeholder="Supplier"
                :error="$v.form.supplier.$error"
              />
              <template #error>
                <div v-if="$v.form.supplier.$dirty && !$v.form.supplier.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Payment method" required>
              <app-select
                v-model="form.supplier_payment_method"
                :options="form.supplier?.payment_methods"
                label="public_name"
                :clearable="false"
                :searchable="false"
                placeholder="Payment method"
                :readonly="!form.supplier"
                :error="$v.form.supplier_payment_method.$error"
              />
              <template #error>
                <div v-if="$v.form.supplier_payment_method.$dirty && !$v.form.supplier_payment_method.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center">
              <app-button ref="submit" :disabled="$v.form.$error" type="submit">Save</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="request_payment_modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('request_payment_modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Request payment for {{ amountFormat(checkedSum) }} TZS</div>
        <div class="popup__body">
          <app-form-group label="Due date" required>
            <app-date-picker
              v-model="form_request.due_date"
              placeholder="Date"
              :error="$v.form_request.due_date.$error"
            />
            <template #error>
              <div v-if="$v.form_request.due_date.$dirty && !$v.form_request.due_date.required">Required field</div>
            </template>
          </app-form-group>
          <app-form-group label="Invoice number" label-for="invoice-number" required>
            <app-input
              v-model="form_request.invoice_number"
              id="invoice-number"
              name="invoice-number"
              placeholder="Invoice number"
              :error="$v.form_request.invoice_number.$error"
            />
            <template #error>
              <div v-if="$v.form_request.invoice_number.$dirty && !$v.form_request.invoice_number.required">Required field</div>
            </template>
          </app-form-group>
          <app-cells :indent="false" position="center">
            <app-button @click.native="onCheckRequestPayment" ref="request_payment">Request</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="delete_invoice_modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete_invoice_modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete invoice
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this invoice?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete_invoice_modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteInvoice" ref="deleteInvoice">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteTransfersRentalCompanyDrivers,
  getAdministrativeSuppliers,
  getCarManagementCarModels,
  getFinanceAccounts,
  getTransfersRentalCarsNew,
  getTransfersRentalCompany,
  getTransfersRentalCompanyCarsList,
  getTransfersRentalCompanyDriversList,
  patchTransfersRentalCompany,
  patchTransfersRentalCompanyCars,
  patchTransfersRentalCompanyDrivers,
  postTransfersRentalCompanyCars,
  postTransfersRentalCompanyDrivers,
  postTransfersRentalCompaniesInvoices,
  getTransfersRentalCompaniesInvoices,
  deleteTransfersRentalCompaniesInvoices,
} from '@/http'
import { format, parse, parseISO } from 'date-fns'
import { required } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'

export default {
  name: 'RentalCompaniesDetail',
  data() {
    return {
      is_loaded: false,
      company: {},
      form: {},
      show_company: true,
      cars_options: [],
      supplier_options: [],
      table_drivers: {
        items: [],
        columns: ['first_name', 'communication', 'communication_other', 'controls'],
        options: {
          headings: {
            'first_name': 'Name',
            'communication': 'Main phone',
            'communication_other': 'Additional phone',
            'controls': '',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false,
        },
      },
      pagination_drivers: {
        pages: 1,
        page: 1,
        count: 1,
      },
      form_driver: {},
      form_car: {},
      is_create: true,
      current_name: '',
      company_drivers: [],
      table_cars: {
        items: [],
        columns: ['car', 'driver', 'controls'],
        options: {
          headings: {
            'car': 'Car',
            'driver': 'Driver',
            'controls': '',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false,
        },
      },
      pagination_cars: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_transfers: {
        items: [],
        columns: [
          'select',
          'price',
          'date',
          'name',
          'from',
          'to',
          'car',
          'driver',
        ],
        options: {
          headings: {
            'select': '',
            'price': 'Price',
            'date': 'Date',
            'name': 'Name',
            'from': 'From',
            'to': 'To',
            'car': 'Car',
            'driver': 'Driver',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false,
        },
      },
      pagination_transfers: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_invoices: {
        items: [],
        columns: [
          'date',
          'supplier',
          'amount',
          'invoice_number',
          'responsible',
          'payment_method',
          'account',
          'payment_plans',
          'attachment',
          'controls'
        ],
        options: {
          headings: {
            'date': 'Date',
            'supplier': 'Supplier',
            'amount': 'Amount',
            'invoice_number': 'Invoice number',
            'responsible': 'Responsible',
            'payment_method': 'Payment method',
            'account': 'Account',
            'payment_plans': 'Payment',
            'attachment': 'Attachment',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_invoices: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_invoices_preloader: false,
      search: '',
      filter: 'false',
      filters: [
        {value: 'false', label: 'Pending'},
        {value: 'true', label: 'Invoiced'},
        {value: '', label: 'All'},
      ],
      table_drivers_preloader: false,
      table_cars_preloader: false,
      table_transfers_preloader: false,
      account_options: [],
      checked_all: false,
      checked_items: [],
      is_show_flying_block: false,
      form_request: {},
    }
  },
  validations: {
    form: {
      name: {required},
      car_models: {required},
      supplier: {required},
      supplier_payment_method: {required},
    },
    form_driver: {
      first_name: {required},
    },
    form_car: {
      car_model: {required},
      plate_number: {required},
    },
    form_request: {
      due_date: {required},
      invoice_number: {required},
    }
  },
  computed: {
    is_transfer_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
        || this.$store.state.userGroups.map(item => item.code).includes('transfer_supervisor')
        || this.$store.state.userGroups.map(item => item.code).includes('transfer_manager')
        || this.$store.state.userGroups.map(item => item.code).includes('transfer_assistant')
    },
    checkedSum() {
      if (this.checked_items.length) {
        return this.checked_items
          .map(item => item.rental_cost)
          .reduce((a, b) => {
            return a + b
          }, 0)
      } else {
        return ''
      }
    },
  },
  watch: {
    checked_all(value) {
      if (value) {
        this.table_transfers.items.forEach(item => {
          if (!this.checked_items.map(item => item.id).includes(item.id) && !item.invoice) this.checked_items.push(item)
        })
      } else {
        this.checked_items = []
      }
    },
    checked_items(value) {
      this.is_show_flying_block = value.length > 0
      if (!this.checked_items.length) this.checked_all = false
    },
  },
  created() {
    Promise.all([
      getTransfersRentalCompany(this.$route.params.id),
      getTransfersRentalCompanyDriversList(this.$route.params.id),
      this.fetchItemsCars(),
      this.fetchItemsTransfers(),
      this.fetchItemsDrivers(),
      this.fetchItemsInvoices()
    ])
      .then(res => {
        this.form = {...res[0].data}
        this.company = res[0].data
        this.company_drivers = res[1].data
        this.is_loaded = true
      })
    getCarManagementCarModels().then(res => {
      this.cars_options = res.data
    })
    getFinanceAccounts().then(response => {
      this.account_options = response.data
    })
  },
  methods: {
    onShowUpdateCompanyPopup() {
      this.$modal.show('update-company')
    },
    async fetchItemsDrivers() {
      this.table_drivers_preloader = true
      const response = await getTransfersRentalCompanyDriversList(this.$route.params.id, {
        page: this.pagination_drivers.page,
        page_size: 20
      })
      this.table_drivers.items = response.data.results
      this.pagination_drivers.pages = response.data.pages
      this.pagination_drivers.count = response.data.count
      this.table_drivers_preloader = false
    },
    async fetchItemsCars() {
      this.table_cars_preloader = true
      const response = await getTransfersRentalCompanyCarsList(this.$route.params.id, {
        page: this.pagination_cars.page,
        page_size: 20
      })
      this.table_cars.items = response.data.results
      this.pagination_cars.pages = response.data.pages
      this.pagination_cars.count = response.data.count
      this.table_cars_preloader = false
    },
    async fetchItemsTransfers() {
      this.table_transfers_preloader = true
      const response = await getTransfersRentalCarsNew({
        page: this.pagination_transfers.page,
        page_size: 20,
        rental_company: this.$route.params.id,
        invoice: this.filter,
        search: this.search,
        ordering: this.filter === 'false' ? 'transfer.pick_up_date,transfer.pick_up_time' : '',
      })
      this.table_transfers.items = response.data.results
      this.pagination_transfers.pages = response.data.pages
      this.pagination_transfers.count = response.data.count
      this.table_transfers_preloader = false
    },
    async fetchItemsInvoices() {
      const response = await getTransfersRentalCompaniesInvoices(this.$route.params.id, {
        page: this.pagination_invoices.page,
        page_size: 20,
      })
      this.table_invoices.items = response.data.results
      this.pagination_invoices.pages = response.data.pages
      this.pagination_invoices.count = response.data.count
      this.table_invoices_preloader = false
    },
    onCheckFormDriver() {
      this.$v.form_driver.$touch()
      if (this.$v.form_driver.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormDriver()
      }
    },
    sendFormDriver() {
      this.$refs.submit_driver.preload = true
      if (this.is_create) {
        postTransfersRentalCompanyDrivers(this.$route.params.id, this.form_driver)
          .then(() => {
            this.fetchItemsDrivers()
            this.form_driver = {}
            this.$modal.hide('driver-form')
            this.$notify({
              type: 'success',
              title: 'Driver added',
            })
          })
          .catch(() => {
            this.$refs.submit_driver.preload = false
          })
      } else {
        patchTransfersRentalCompanyDrivers(this.$route.params.id, this.form_driver.id, this.form_driver)
          .then(() => {
            this.fetchItemsDrivers()
            this.form_driver = {}
            this.$modal.hide('driver-form')
            this.$notify({
              type: 'success',
              title: 'Driver saved',
            })
          })
          .catch(() => {
            this.$refs.submit_driver.preload = false
          })
      }
    },
    onCheckFormCar() {
      this.$v.form_car.$touch()
      if (this.$v.form_car.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormCar()
      }
    },
    sendFormCar() {
      this.$refs.submit_car.preload = true
      if (this.is_create) {
        postTransfersRentalCompanyCars(this.$route.params.id, this.normalizeCarForm())
          .then(() => {
            this.fetchItemsCars()
            this.form_car = {}
            this.$modal.hide('car-form')
            this.$notify({
              type: 'success',
              title: 'Car added',
            })
          })
          .catch(() => {
            this.$refs.submit_car.preload = false
          })
      } else {
        patchTransfersRentalCompanyCars(this.$route.params.id, this.form_car.id, this.normalizeCarForm())
          .then(() => {
            this.fetchItemsCars()
            this.form_car = {}
            this.$modal.hide('car-form')
            this.$notify({
              type: 'success',
              title: 'Car saved',
            })
          })
          .catch(() => {
            this.$refs.submit_car.preload = false
          })
      }
    },
    normalizeCarForm() {
      const form = {...this.form_car}

      if (form.car_model && typeof form.car_model !== 'number') form.car_model = form.car_model.id
      if (form.driver && typeof form.driver !== 'number') form.driver = form.driver.id
      return form
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      patchTransfersRentalCompany(this.$route.params.id, this.normalizeForm())
        .finally(() => {
          this.$refs.submit.preload = false
        })
        .then(res => {
          this.form = {...res.data}
          this.company = res.data
          this.$modal.hide('update-company')
          this.$notify({
            type: 'success',
            title: 'Rental company saved',
          })
        })

    },
    onCheckRequestPayment() {
      this.$v.form_request.$touch()
      if (this.$v.form_request.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.$refs.request_payment.preload = true
        postTransfersRentalCompaniesInvoices(
          this.$route.params.id,
          {...this.form_request, transfers: this.checked_items.map(item => item.id)}
        )
          .then(() => {
            this.fetchItemsTransfers()
            this.fetchItemsInvoices()
            this.$modal.hide('request_payment_modal')
            this.checked_items = []
          })
          .catch(() => { this.$refs.request_payment.preload = false })
      }
    },
    onShowModalRequestPayment() {
      this.form_request = {}
      this.$v.form_request.$reset()
      this.$modal.show('request_payment_modal')
    },
    normalizeForm() {
      const form = {...this.form}

      if (form.car_models.every(item => typeof item === 'object')) {
        form.car_models = form.car_models.map(item => item.id)
      }
      form.supplier = form.supplier.id
      form.supplier_payment_method = form.supplier_payment_method.id
      return form
    },
    onCopyContactLink(link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => {
          console.error('Something went wrong')
        })
    },
    onClickPaginationTransfers(page) {
      this.pagination_transfers.page = page
      this.fetchItemsTransfers()
    },
    onClickPaginationDrivers(page) {
      this.pagination_drivers.page = page
      this.fetchItemsDrivers()
    },
    onClickPaginationCars(page) {
      this.pagination_cars.page = page
      this.fetchItemsCars()
    },
    onClickPaginationInvoices() {
      this.pagination_invoices.page = page
      this.fetchItemsInvoices()
    },
    onShowDeleteInvoiceModal(id) {
      this.invoice_to_action = id
      this.$modal.show('delete_invoice_modal')
    },
    onDeleteInvoice() {
      this.$refs.deleteInvoice.preload = true
      deleteTransfersRentalCompaniesInvoices(this.$route.params.id, this.invoice_to_action)
        .then(() => {
          this.fetchItemsInvoices()
          this.$notify({
            type: 'success',
            title: 'Invoice deleted',
          })
          this.$modal.hide('delete_invoice_modal')
        })
        .catch(() => { this.$refs.deleteInvoice.preload = false })
    },
    onShowCreateModal(name) {
      this.is_create = true
      this.$v[`form_${name}`].$reset()
      this.$modal.show(`${name}-form`)
    },
    onShowEditDriverModal(row) {
      this.is_create = false
      this.form_driver = {...row}
      this.$modal.show('driver-form')
    },
    onShowEditCarModal(row) {
      this.is_create = false
      this.form_car = {...row}
      this.$modal.show('car-form')
    },
    onDeleteItem() {
      this.$refs.delete_button.preload = true
      if (this.current_name === 'driver') {
        deleteTransfersRentalCompanyDrivers(this.$route.params.id, this.form_driver.id)
          .then(() => {
            this.fetchItemsDrivers()
            this.$modal.hide('delete-modal')
            this.$notify({
              type: 'success',
              title: 'Driver deleted',
            })
          })
          .catch(() => {
            this.$refs.delete_button.preload = false
          })
      }
    },
    onSupplierSelected() {
      this.form.supplier_payment_method = null
    },
    onFilterChange() {
      this.fetchItemsTransfers()
    },
    onListSearch(search) {
      this.searchList(search, this)
    },
    searchList: debounce((search, vm) => {
      vm.pagination_transfers.page = 1
      vm.fetchItemsTransfers()
    }, 350),
    clientTableList(clients) {
      let result = ''

      for (const key in clients) {
        result += `${clients[key].first_name} ${clients[key].last_name}<br>`
      }
      return result
    },
    amountFormat(amount) {
      return amount ? new Intl.NumberFormat('ru-RU').format(amount) : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getAdministrativeSuppliers({search}).then(response => {
          vm.supplier_options = response.data
          loading(false)
        })
      }
    }, 350),
  },
}
</script>

<style lang="sass" scoped>
.company__wrapper
  max-width: 950px

.slide-small-enter-active
  transition-duration: 0.3s
  transition-timing-function: ease-in

.slide-small-leave-active
  transition-duration: 0.3s
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1)

.slide-small-enter-to, .slide-small-leave
  max-height: 147px
  overflow: hidden

.slide-small-enter, .slide-small-leave-to
  overflow: hidden
  max-height: 0
</style>
